/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  GetWorkflowOperatorsV2Params,
  TLAvailableOperatorsResponse,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get Workflow Operators V2
 */
export const getWorkflowOperatorsV2 = (
  params?: GetWorkflowOperatorsV2Params,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLAvailableOperatorsResponse>(
    { url: `/api/v2/workflows/operators`, method: 'GET', params, signal },
    options,
  );
};

export const getGetWorkflowOperatorsV2QueryKey = (params?: GetWorkflowOperatorsV2Params) => {
  return ['api', 'v2', 'workflows', 'operators', ...(params ? [params] : [])] as const;
};

export const getGetWorkflowOperatorsV2QueryOptions = <
  TData = Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowOperatorsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetWorkflowOperatorsV2QueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>> = ({ signal }) =>
    getWorkflowOperatorsV2(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetWorkflowOperatorsV2QueryResult = NonNullable<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>>;
export type GetWorkflowOperatorsV2QueryError = ErrorType<TLHTTPValidationError>;

export function useGetWorkflowOperatorsV2<
  TData = Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | GetWorkflowOperatorsV2Params,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowOperatorsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowOperatorsV2<
  TData = Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowOperatorsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
          TError,
          Awaited<ReturnType<typeof getWorkflowOperatorsV2>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetWorkflowOperatorsV2<
  TData = Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowOperatorsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Workflow Operators V2
 */

export function useGetWorkflowOperatorsV2<
  TData = Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: GetWorkflowOperatorsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetWorkflowOperatorsV2QueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Workflow Operators V2
 */
export const prefetchGetWorkflowOperatorsV2Query = async <
  TData = Awaited<ReturnType<typeof getWorkflowOperatorsV2>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: GetWorkflowOperatorsV2Params,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkflowOperatorsV2>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetWorkflowOperatorsV2QueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Workflow Operators V2
 */
export const invalidateGetWorkflowOperatorsV2 = async (
  queryClient: QueryClient,
  params?: GetWorkflowOperatorsV2Params,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetWorkflowOperatorsV2QueryKey(params) }, options);

  return queryClient;
};
